import { useCallback, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, useSettings } from 'core/hooks/settings/useSettings'
import useNavigation from 'core/hooks/navigation/useNavigation'
import { matchPath } from 'react-router'
import {
  AcceptInviteUrl,
  AccountantsUrl,
  AccountUrl,
  ComplianceOverviewUrl,
  ComplianceUrl,
  DashboardUrl,
  DataExportsUrl,
  MaintenanceUrl,
  PremiumUrl,
  ReportDetailUrl,
  ReportsUrl,
  SignUpAboutYouUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SignUpStepOneUrl,
  SignUpStepThreeUrl,
  SignUpStepTwoUrl,
  SplashMessageUrl,
  StartUrl,
  TeamDashboardUrl,
  TeamsFeatureUrl,
  TransactionsUrl,
  TripsUrl
} from 'constants/RouteUrls'
import { useUserProfile } from 'core/hooks/data/useDataHook'
import APIService from 'api/APIService'
import useNotifications from 'core/hooks/ui/useNotifications'
import { docCookies } from 'utils/docCookies'
import AnalyticsHelper from 'utils/AnalyticsHelper'
import { USER_ENDPOINTS } from 'api/ApiRoutes'

/**
 * Return type for the useAuth hook
 */
export interface UseAuthReturn {
  /** Whether the user is currently authenticated */
  isAuthenticated: boolean
  /** Whether authentication is currently loading */
  isAuthLoading: boolean
  /** The current user's token ID */
  userTokenID: string | null
  /** Function to fetch user token ID from session */
  fetchUserTokenIDFromSession: () => Promise<void>
  /** Function to switch to a different user session */
  switchSession: (authToken: string) => Promise<void>
  /** Function to destroy the current session */
  destroySession: () => Promise<void>
  /** Function to set the user ID */
  setUserId: (userId: string | null) => void
  /** Function to set the motus access/refresh tokens */
  setMotusTokens: (args: {
    accessToken: string | null
    refreshToken: string | null
  }) => void
  /** Function to perform a complete logout operation, clearing all authentication state, tokens, and cached data */
  logout: (args?: { event?: string; redirect?: boolean }) => void
  /** Function to manually set the authentication loading state */
  setAuthLoading: (isLoading: boolean) => void
}

const DEFAULT_AUTH_SETTINGS: Settings['auth'] = {
  userTokenID: null,
  isAuthLoading: false,
  isAuthenticated: false,
  accessToken: null,
  refreshToken: null
}

/**
 * Array of routes that require authentication
 */
const privateRoutes = [
  AccountantsUrl,
  DashboardUrl,
  PremiumUrl,
  AccountUrl,
  TeamsFeatureUrl,
  TransactionsUrl,
  DataExportsUrl,
  TripsUrl,
  ReportsUrl,
  ComplianceOverviewUrl,
  ComplianceUrl,
  '/team',
  TeamDashboardUrl,
  SignUpAboutYouUrl,
  SignUpStepOneUrl,
  SignUpStepTwoUrl,
  SignUpStepThreeUrl,
  SignUpCreateTeamUrl,
  SignUpInviteUrl,
  SplashMessageUrl,
  AcceptInviteUrl,
  ReportDetailUrl
]

const endpointExceptions = [USER_ENDPOINTS.INTERCOM_TOKEN]

/**
 * Custom hook for managing authentication state and operations
 *
 * This is the central authentication hook that handles all authentication-related concerns in the application.
 * It manages both standard Everlance sessions and Motus token-based authentication.
 *
 * @remarks
 * **Core Responsibilities:**
 * - Manages authentication state (isAuthenticated, isAuthLoading, userTokenID)
 * - Handles user profile fetching and updates
 * - Manages user session lifecycle (creation, switching, destruction)
 * - Handles Motus-specific token management (accessToken and refreshToken)
 * - Provides route-based authentication validation
 * - Sets up global 401 error interceptor for automatic session expiration handling
 * - Integrates with analytics for tracking authentication events
 *
 * **Session Types:**
 * - Standard Everlance sessions: Uses server-side session with userTokenID
 * - Motus sessions: Uses client-side tokens (accessToken/refreshToken) stored in cookies
 *
 * **Authentication Flow:**
 * 1. On mount (if validateAuth=true): Fetches current user profile from backend
 * 2. On success: Updates authentication state and redirects if needed
 * 3. On failure (401/422): Clears authentication and redirects to login
 * 4. Sets up response interceptor to catch 401 errors globally
 *
 * **Private Routes:**
 * The hook automatically determines if the current route requires authentication and
 * redirects unauthenticated users to the start page.
 *
 * @param options - Configuration options for the hook
 * @param options.validateAuth - Whether to validate authentication on mount - only used in RouteLayout (the entry point of the app)
 * @returns Object containing authentication state and functions
 *
 * @example
 * ```tsx
 * // At the app root level
 * const { isAuthenticated, userTokenID, destroySession } = useAuth({ validateAuth: true });
 *
 * // In child components
 * const { isAuthenticated, logout } = useAuth();
 *
 * // Motus authentication
 * const { setMotusTokens } = useAuth();
 * setMotusTokens({ accessToken: 'token123', refreshToken: 'refresh456' });
 * ```
 */
const useAuth = (
  { validateAuth }: { validateAuth: boolean } = { validateAuth: false }
): UseAuthReturn => {
  const { data: settings, set, reset: resetSettings } = useSettings()
  const { setData } = useUserProfile()
  const notifications = useNotifications()
  const {
    auth: {
      isAuthenticated,
      isAuthLoading,
      userTokenID,
      accessToken,
      refreshToken
    }
  } = settings
  const { pathname, replace, search } = useNavigation()
  const isAuthPath = pathname.includes('auth')
  const isMaintenancePath = pathname.includes('maintenance')
  const queryClient = useQueryClient()

  /**
   * Determines if the current route requires authentication
   * Uses memoization to avoid recalculating on every render
   */
  const isPrivateRoute = useMemo(
    () =>
      !!privateRoutes.find((path) => {
        const m = matchPath(path, pathname)
        return path === m?.pattern.path
      }),
    [pathname]
  )

  /**
   * Sets the user accessToken and refreshToken
   *
   * @param accessToken - The access token to set, or null to clear
   * @param refreshToken - The refresh_token to set, or null to clear
   */
  const setMotusTokens = useCallback(
    ({
      accessToken,
      refreshToken
    }: {
      accessToken: string | null
      refreshToken: string | null
    }) => {
      let _isAuthenticated = false
      let _accessToken = docCookies.getItem('motus_access_token')
      let _refreshToken = docCookies.getItem('motus_refresh_token')

      // Both will be present on login
      if (accessToken && refreshToken) {
        docCookies.setItem('motus_access_token', accessToken)
        docCookies.setItem('motus_refresh_token', refreshToken)

        // Drop any stale Everlance session id so a page refresh does not
        // re-hydrate userTokenID and hit Everlance staging via fetchUser().
        localStorage.removeItem('user-token-id')

        _accessToken = accessToken
        _refreshToken = refreshToken
        _isAuthenticated = true
      }

      // When only accessToken is present - the accessToken has been refreshed
      if (!refreshToken && accessToken) {
        docCookies.setItem('motus_access_token', accessToken)

        localStorage.removeItem('user-token-id')

        _accessToken = accessToken
        _isAuthenticated = true
      }

      // logout action
      if (!refreshToken && !accessToken) {
        docCookies.removeItem('motus_access_token')
        docCookies.removeItem('motus_refresh_token')
        _accessToken = null
        _refreshToken = null
      }

      set('auth', {
        isAuthenticated: _isAuthenticated,
        isAuthLoading: false,
        userTokenID: null,
        accessToken: _accessToken,
        refreshToken: _refreshToken
      })
    },
    [set]
  )

  /**
   * Mutation for fetching user profile data - Everlance specific
   * Handles success and error cases, updates authentication state
   */
  const { isPending: isFetchingUser, mutate: fetchUser } = useMutation({
    mutationKey: ['users', 'me', 'mutation'],
    mutationFn: async () => {
      return await APIService.everlance.getCurrentUser()
    },
    onSuccess: (user) => {
      // If successfully fetched user data, update the user profile and session storage to the useUserProfile data hook
      setData(user)
      localStorage.setItem('user-token-id', user.token_id as string)

      setTimeout(() => {
        // Update authentication state in settings after a short delay to ensure UI updates
        set('auth', {
          isAuthenticated: true,
          isAuthLoading: false,
          userTokenID: user.token_id as string
        })
        if (isAuthPath) {
          replace(
            user?.teams?.[0]?.roles?.length ? TeamDashboardUrl : DashboardUrl
          )
        }
      }, 500)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      // Network or CORS error

      const isNetworkError =
        !error.response && error.message === 'Network Error'

      if (isNetworkError) {
        setTimeout(() => {
          notifications.error({
            title: 'Network Error',
            subtitle:
              'Unable to connect to the server. Please check your internet connection or try again later.',
            id: 'network-error'
          })
        }, 500)
      }

      if (
        error?.response?.status === 401 ||
        error?.response?.status === 422 ||
        isNetworkError
      ) {
        set('auth', DEFAULT_AUTH_SETTINGS)
        if (!isAuthPath && isPrivateRoute) {
          replace(StartUrl)
        }
      }
    }
  })

  /**
   * Mutation for fetching user profile data - Motus specific
   * Handles success and error cases, updates authentication state
   */
  const { mutate: fetchMotusUser } = useMutation({
    mutationKey: ['users', 'me', 'mutation'],
    mutationFn: async (accessToken: string) => {
      return await APIService.motus.getCurrentUser({ accessToken })
    },
    onSuccess: (user) => {
      // If successfully fetched user data, update the user profile and session storage to the useUserProfile data hook
      setData(user)

      setTimeout(() => {
        set('auth', {
          ...DEFAULT_AUTH_SETTINGS,
          isAuthenticated: true,
          isAuthLoading: false,
          accessToken,
          refreshToken
        })

        if (isAuthPath) {
          replace(DashboardUrl)
        }
      }, 300)
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (error: any) => {
      setMotusTokens({
        accessToken: null,
        refreshToken: null
      })

      // Network or CORS error
      const isNetworkError =
        !error.response && error.message === 'Network Error'

      if (isNetworkError) {
        setTimeout(() => {
          notifications.error({
            title: 'Network Error',
            subtitle:
              'Unable to connect to the server. Please check your internet connection or try again later.',
            id: 'network-error'
          })
        }, 500)
      }

      // Only show notification if not already on an auth path
      if (!isAuthPath) {
        notifications.error({
          title: 'Session Expired',
          subtitle: 'Your session has expired. Please log in again.',
          id: 'session-expired'
        })
      }
      if (!isAuthPath && isPrivateRoute) {
        replace(StartUrl)
      }
    }
  })

  /**
   * Sets the user ID and updates authentication state
   *
   * @param userId - The user ID to set, or null to clear
   */
  const setUserId = useCallback(
    (userId: string | null) => {
      if (userId) localStorage.setItem('user-token-id', userId)
      if (!userId) localStorage.removeItem('user-token-id')

      set('auth', {
        ...DEFAULT_AUTH_SETTINGS,
        isAuthenticated: !!userId,
        isAuthLoading: false,
        userTokenID: userId
      })
    },
    [set]
  )

  /**
   * Manually sets the authentication loading state
   *
   * @param isLoading - The loading state to set
   */
  const setAuthLoading = useCallback(
    (isLoading: boolean) => {
      set('auth', {
        ...settings.auth,
        isAuthLoading: isLoading
      })
    },
    [set, settings.auth]
  )

  /**
   * Performing a request with the "Authorization" header set to the given auth token will force the backend to change the user in the session
   *
   * @param authToken - The authentication token for the new session
   */
  const switchSessionMutation = useMutation({
    mutationFn: async (authToken: string): Promise<void> => {
      await APIService.everlance.switchSessions({ authToken })
      fetchUser()
    }
  })

  /**
   * Destroys the current session by making a DELETE request to the backend
   *
   * Invalidates the user query cache and optionally removes the user token from sessionStorage.
   * This is typically called during logout or when switching sessions.
   * ```
   */
  const destroySession = useCallback(async (): Promise<void> => {
    await APIService.everlance.destroySession()
    queryClient.invalidateQueries({ queryKey: ['users', 'me'] })
    localStorage.removeItem('user-token-id')
  }, [queryClient])

  /**
   * Fetches user token ID from session
   * Prevents multiple simultaneous fetch requests
   *
   * @returns Promise that resolves when fetch is complete
   */
  const fetchUserTokenIDFromSession = useCallback(async (): Promise<void> => {
    if (isFetchingUser) return
    fetchUser()
  }, [fetchUser, isFetchingUser])

  /**
   * Switches to a different user session
   * Wrapper around the switchSessionMutation
   *
   * @param auth_token - The authentication token for the new session
   * @returns Promise that resolves when session switch is complete
   */
  const switchSession = useCallback(
    async (auth_token: string): Promise<void> => {
      switchSessionMutation.mutate(auth_token)
    },
    [switchSessionMutation]
  )

  /**
   * Performs a complete logout operation and cleans up all authentication state
   *
   * This function handles the complete logout flow for both Everlance and Motus sessions.
   * It ensures all authentication state, tokens, settings, and cached data are properly cleared.
   */
  const logout = useCallback(
    (params?: { event?: string; redirect?: boolean }) => {
      const { event = 'Sign Out', redirect = true } = params ?? {}
      redirect && replace(StartUrl)
      destroySession()
      AnalyticsHelper.trackEvent(event)
      AnalyticsHelper.clearUserCookie()
      setMotusTokens({
        accessToken: null,
        refreshToken: null
      })
      resetSettings()
      sessionStorage.clear()
      queryClient.removeQueries()
      setTimeout(() => {
        setUserId(null)
      }, 300)
    },
    [
      destroySession,
      queryClient,
      replace,
      resetSettings,
      setMotusTokens,
      setUserId
    ]
  )

  /**
   * Effect hook that validates authentication on mount if validateAuth is true
   *
   * This effect runs once on component mount and is responsible for checking if the user
   * has an active session. It supports dual authentication systems (Everlance and Motus)
   * and handles JWT-based authentication flows.
   */
  useEffect(() => {
    const jwt = new URLSearchParams(search).get('jwt') ?? ''

    if (!validateAuth) return

    /**
     * Everlance Flow: Validate session-based authentication
     * When userTokenID exists in sessionStorage and no JWT is in the URL:
     * - Calls fetchUser() to validate the session by fetching current user profile
     * - On success: Updates authentication state and user profile data
     * - On failure: Clears auth state and redirects to login if on a private route
     */
    /**
     * Motus takes precedence: when a Motus accessToken exists, validate that
     * session and skip the Everlance flow. A stale `user-token-id` left in
     * localStorage must not resurrect the Everlance branch and hit Everlance
     * staging instead of the Motus translation layer.
     */
    if (accessToken) {
      fetchMotusUser(accessToken)
    } else if (userTokenID && !jwt) {
      fetchUser()
    }

    /**
     * No Session: Reset authentication state
     * When BOTH accessToken and userTokenID are missing (neither Everlance nor Motus session exists):
     * - Resets authentication state to defaults (unauthenticated)
     * - This ensures clean state when no valid session exists
     */
    if (!accessToken && !userTokenID) {
      set('auth', DEFAULT_AUTH_SETTINGS)
    }

    /**
     * JWT Flow: Handle JWT-based authentication (SAML/SSO)
     * When a jwt query parameter is present in the URL:
     * - Skips session validation (fetchUser() and fetchMotusUser() are not called above)
     * - Explicitly sets authentication state to false (not authenticated, not loading)
     * - Allows the JWT to be processed by specialized handlers (e.g., SAML callback page)
     * - Setting isAuthenticated: false shows unauthenticated state while JWT is processed elsewhere
     */
    if (jwt) {
      set('auth', {
        ...DEFAULT_AUTH_SETTINGS,
        isAuthenticated: false,
        isAuthLoading: false,
        userTokenID: null
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Effect hook that sets up HTTP response interceptors for global error handling
   *
   * This effect runs once on mount and establishes interceptors on both Everlance and generic
   * Axios instances to handle HTTP errors consistently across the application.
   * The interceptors monitor all API responses and handle authentication and server errors.
   *
   * Dependencies: Recreates interceptors when any of the following change:
   * - isAuthPath: Whether user is on an auth-related route
   * - isMaintenancePath: Whether user is on the maintenance page
   * - isPrivateRoute: Whether the current route requires authentication
   * - notifications: Notification service for showing error messages
   * - pathname: Current URL path
   * - replace: Navigation function for redirects
   * - set: Settings update function for auth state
   */
  useEffect(() => {
    /**
     * Everlance Axios Instance Interceptor
     * Handles responses from Everlance-specific API endpoints
     */
    APIService.everlanceAxiosInstance.interceptors.response.use(
      (config) => {
        // Success case: Pass through successful responses unchanged
        return config
      },
      (error) => {
        /**
         * 503 Service Unavailable
         * 504 Gateway Timeout Error Handling
         * When the server is unreachable or times out:
         * - Redirects to start page unless already on maintenance page
         * - Early return prevents further error processing
         */
        if (
          [503, 504].includes(error?.status) &&
          !isMaintenancePath &&
          !endpointExceptions.some((item) =>
            (error.request?.responseURL ?? '').endsWith(item)
          )
        ) {
          replace(MaintenanceUrl)
          return
        }

        /**
         * 401 Unauthorized Error Handling
         * When the user's session is invalid or expired:
         * - Clears all authentication state to defaults (logged out state)
         * - Shows "Session Expired" notification if not on auth pages (prevents duplicate notifications)
         * - Redirects to login page if on a private route and not already on auth pages
         * - Removes userTokenID from sessionStorage to ensure clean logout
         */
        if (error?.status === 401) {
          set('auth', {
            ...DEFAULT_AUTH_SETTINGS,
            isAuthenticated: false,
            isAuthLoading: false,
            userTokenID: null
          })

          // Only show notification if not already on an auth path
          if (!isAuthPath) {
            notifications.error({
              title: 'Session Expired',
              subtitle: 'Your session has expired. Please log in again.',
              id: 'session-expired'
            })
          }

          if (!isAuthPath && isPrivateRoute) {
            replace(StartUrl)
            localStorage.removeItem('user-token-id')
          }
        }

        // Always return the error to allow further handling if needed
        return Promise.reject(error)
      }
    )

    /**
     * Generic Axios Instance Interceptor
     * Handles responses from other API endpoints (e.g., Motus)
     * Currently only handles 504 errors, as 401 handling is specific to Everlance sessions
     */
    APIService.axiosInstance.interceptors.response.use(
      (config) => {
        // Success case: Pass through successful responses unchanged
        return config
      },
      (error) => {
        /**
         * 503 Service Unavailable
         * 504 Gateway Timeout Error Handling
         * When the server is unreachable or times out:
         * - Redirects to start page unless already on maintenance page
         * - Early return prevents further error processing
         */
        if (
          [503, 504].includes(error?.status) &&
          !isMaintenancePath &&
          !endpointExceptions.some((item) =>
            (error.request?.responseURL ?? '').endsWith(item)
          )
        ) {
          replace(MaintenanceUrl)
          return
        }
        // Always return the error to allow further handling if needed
        return Promise.reject(error)
      }
    )
  }, [
    isAuthPath,
    isMaintenancePath,
    isPrivateRoute,
    notifications,
    pathname,
    replace,
    set
  ])

  return {
    isAuthenticated,
    isAuthLoading,
    userTokenID,
    fetchUserTokenIDFromSession,
    switchSession,
    destroySession,
    setUserId,
    setMotusTokens,
    logout,
    setAuthLoading
  }
}

export default useAuth
